/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useRef } from 'react';

import { gaReportEvent } from 'sarsaparilla';

import { resolveWaitMs } from './countdown';

/** The data-layer event name, so every microfrontend reports under one name */
const EVENT = 'rate_limit_notice';

/**
 * What the notice did, which is the whole of the funnel: how many visitors saw it,
 * how many acknowledged rather than waiting, and how often recovery ran.
 *
 * Automatic and manual recovery are separate actions so the reporting can tell the
 * notice recovering the page for the visitor (`auto_reloaded`) from the visitor
 * recovering it themselves with the reload button (`manual_reloaded`).
 */
type RateLimitAction = 'shown' | 'acknowledged' | 'auto_reloaded' | 'manual_reloaded';

/**
 * Reports what the notice did, so FE Core can tell whether the feature is helping
 * or misfiring. The README's "What it reports" has the reasoning; this is the shape.
 *
 * `has_server_wait` says whether the wait reported alongside it is the server's own,
 * so it is false for a value the module could not use — a negative or absurd
 * `retry-after` falls back to the default like an absent one, and reporting that as
 * server-supplied would blur the one distinction the field exists to draw.
 *
 * Nothing is gated on the event reaching the analytics backend. The data layer is
 * processed asynchronously, so an `auto_reloaded` beacon races the navigation it
 * describes and some will be lost: that count is a floor. Holding the reload until
 * the beacon is acknowledged would make recovering the page depend on an analytics
 * tag loading, and a page that never recovers is worse than a lost event.
 */
export function reportRateLimitEvent(
    action: RateLimitAction,
    retryAfterMs: number | null
) {
    const waitMs = resolveWaitMs(retryAfterMs);

    gaReportEvent({
        event: {
            event: EVENT,
            category: 'Rate Limit',
            action,
            wait_seconds: Math.round(waitMs / 1000),
            has_server_wait: retryAfterMs === waitMs,
        },
    });
}

/**
 * Reports the notice being shown, once per occurrence.
 *
 * Keyed on the notice actually being visible rather than on a 429 being detected,
 * so the events describe what a visitor saw: a burst of 429s collapses into one
 * notice and reports one `shown`, and a notice the launch kill switch suppressed
 * reports nothing at all.
 *
 * The wait reported is the one the notice opened on. A later 429 in the same burst
 * can fill in a wait the first one lacked, and that is not a second showing — the
 * visitor saw one notice, and firing once per occurrence is what makes the count
 * mean anything. The cost is that `shown` can say the server named no wait where
 * the same occurrence's `acknowledged` or `auto_reloaded` says it did.
 */
export function useNoticeShownEvent(isVisible: boolean, retryAfterMs: number | null) {
    const hasReportedRef = useRef(false);

    useEffect(() => {
        if (!isVisible) {
            // Armed again for the next occurrence, which a 429 after the suppression
            // window has elapsed is
            hasReportedRef.current = false;
            return;
        }

        if (hasReportedRef.current) {
            return;
        }

        hasReportedRef.current = true;
        reportRateLimitEvent('shown', retryAfterMs);
    }, [isVisible, retryAfterMs]);
}
